<template>
  <section v-if="loaded">
    <v-btn
      dark
      color="primary"
      class="video-add-btn"
      @click="openModal('add')"
      v-if="isWriteAvailable"
    >
      Add new video
    </v-btn>

    <!-- VIDEOS LIST -->
    <div v-if="getVideos.length">
    <v-card class="video"
      v-for="(video, i) in getVideos"
      :key="`video-${i}`"
    >
      <div class="video__info">
        <a :href="getVideoLink(video)" target="_blank">
          {{ getTextFromHTMLStr(getVideoTitle(video)) }}
        </a>
        <p class="video__desc">
          {{ getVideoDesc(video) }}
        </p>
      </div>
      <div class="video__controls">
        <div class="video__controls-status">
          <v-switch
            :label="`Status: ${getVideoStatus(video)}`"
            v-model="video.isVisible"
            @click.native="changeVideoVisibility(video)"
          />
        </div>
        <v-btn color="primary" fab small dark @click="openModal('edit', video)" :disabled="!isWriteAvailable">
          <v-icon>edit</v-icon>
        </v-btn>
        <v-btn color="error" fab small dark @click="askForDeleteVideo(video)" :disabled="!isWriteAvailable">
          <v-icon>delete</v-icon>
        </v-btn>
      </div>
    </v-card>
    </div>
    <!-- VIDEOS LIST -->

    <!-- FORM MODAL -->
    <v-dialog
      v-model="modal.visibility"
      max-width="500px"
      :persistent="true"
    >
      <v-card>
        <v-card-title>
          <h2 v-if="modal.mode === 'add'">Add video</h2>
          <h2 v-else>Edit {{ getTextFromHTMLStr(videoForm.title) }}</h2>
        </v-card-title>
        <v-card-text>
          <content-editor
            label="Title"
            v-model="videoForm.title"
            :error="$v.videoForm.title"
          />
          <content-field
            label="Url"
            v-model="videoForm.url"
            :error="$v.videoForm.url"
          />
          <content-field
            label="Description"
            v-model="videoForm.description"
            :error="$v.videoForm.description"
            :multiLine="true"
          />

          <!-- <v-select
            :items="sessions"
            v-model="videoForm.relatedSessionId"
            :error="isSessionRequiredError"
            label="Related session"
            item-text="title"
            item-value="id"
            track-by="title"
            no-data-text="a"
            :key="videoForm.relatedSessionId"
            autocomplete
          /> -->

          <v-select
            :items="sessions"
            v-model="videoForm.relatedSessionId"
            label="Related session"
            item-text="title"
            item-value="id"
            track-by="title"
            no-data-text="a"
            :key="videoForm.relatedSessionId"
            autocomplete
          />

          <div v-if="getSessionById(videoForm.relatedSessionId)" class="selected-related-session">
            <h4>Selected session:</h4>
            <div>
              <div v-if="videoForm.relatedSessionId && getSessionById(videoForm.relatedSessionId)">
                <span><strong>Date:</strong></span>
                <span>{{ getDate(getSessionById(videoForm.relatedSessionId).startTime) }}</span>

                <span>–</span>
                <span>{{ getDate(getSessionById(videoForm.relatedSessionId).endTime) }}</span>
              </div>
              <div>
                <span><strong>Speakers:</strong></span>
                <span>{{ getSpeakers(getSessionById(videoForm.relatedSessionId).speakers) }}</span>
              </div>
            </div>
          </div>

          <v-switch
            :label="`Status: ${getVideoStatus(videoForm)}`"
            v-model="videoForm.isVisible"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" @click="saveForm">Save</v-btn>
          <v-btn color="primary" @click="closeModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- FORM MODAL -->

    <!-- DELETE CONFIRMATION MODAL -->
    <v-dialog persistent v-model="deleteConfirmationModal.visibility" max-width="300px">
      <v-card>
        <v-card-title>
          <h2>Delete {{ deleteConfirmationModal.video.title }}?</h2>
        </v-card-title>
        <v-card-actions>
          <v-spacer />
          <v-btn color="error" @click="onDeleteVideo(deleteConfirmationModal.video.id)">Delete</v-btn>
          <v-btn color="primary" @click="hideDeleteConfirmationModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- DELETE CONFIRMATION MODAL -->
  </section>
</template>

<script>
  import { required, url } from 'vuelidate/lib/validators'
  import { mapActions, mapGetters } from 'vuex'
  import { pathOr, clone } from 'ramda'
  import moment from 'moment' // lib to format date and time
  import mixins from '@/utils/mixins'
  import getTextFromHTMLStr from '@/utils/getTextFromHTMLStr'

  export default {
    name: 'VideosList',
    mixins: [mixins.isWriteAvailable],
    data () {
      return {
        loaded: false,
        editableItemId: null,
        videoForm: {
            title: '',
            description: '',
            relatedSessionId: '',
            isVisible: true,
            url: ''
          },
        modal: {
          visibility: false,
          mode: '' // 'edit', 'add'
        },
        deleteConfirmationModal: {
          video: {
            title: '',
            id: ''
          },
          visibility: false
        },
        // DON'T DELETE! need for isWriteAvailable mixin
        scopeForWrite: 'general_settings:write'
      }
    },
    validations: {
      videoForm: {
        title: { required },
        description: { required },
        // relatedSessionId: { required },
        url: { required, url }
      }
    },
    computed: {
      ...mapGetters([
        'getVideos',
        'sessionsList',
        'getSessionById'
      ]),
      sessions () {
        return this.sessionsList.reduce(function (acc, session) {
          if (session.status !== 'Deleted'){
            const clonedSession = clone(session)
            clonedSession.title = clonedSession.title || 'No title'
            acc.push(clonedSession)
          }
          return acc
        },[])
      },
      isSessionRequiredError () {
        return !!(this.$v.videoForm.relatedSessionId.$dirty && this.$v.videoForm.relatedSessionId.$invalid)
      }
    },
    methods: {
      ...mapActions([
        'fetchVideos',
        'deleteVideo',
        'updateVideo',
        'createVideo',
        'getSessionsList',
        'fetchSpeakersList'
      ]),
      getTextFromHTMLStr,
      openModal (mode, editableVideo) {
        this.resetForm()
        this.modal.mode = mode
        this.modal.visibility = true

        if (mode === 'edit' && editableVideo) {
          this.editableItemId = editableVideo.id
          this.setForm(editableVideo)
        }
      },
      closeModal () {
        this.modal.visibility = false
        setTimeout(() => {
          this.modal.mode = null
          this.editableVideo = null
          this.editableItemId = ''
        }, 200)
      },
      async saveForm () {
        this.$v.$touch()
        if(this.$v.videoForm.$invalid) return
        if (this.modal.mode === 'add') {
          try {
            await this.createVideo(this.videoForm)
            this.closeModal()
          } catch (err) {
            throw err
          }
        } else {
          this.editVideo()
        }
      },
      async editVideo () {
        const payload = {
          id: this.editableItemId,
          ...this.videoForm
        }
        try {
          await this.updateVideo(payload)
          this.closeModal()
        } catch (err) {
          throw err
        }
      },
      resetForm () {
        this.videoForm.title = ''
        this.videoForm.isVisible = true
        this.videoForm.description = ''
        this.videoForm.relatedSessionId = ''
        this.videoForm.url = ''
        this.$v.$reset()
      },
      setForm (editableVideo) {
        this.videoForm = { ...editableVideo }
      },
      getVideoLink (video) {
        return pathOr('', ['url'], video)
      },
      getVideoTitle (video) {
        return pathOr('', ['title'], video)
      },
      getVideoStatus (video) {
        return pathOr(false, ['isVisible'], video)
          ? 'Visible'
          : 'Hiden'
      },
      getVideoDesc (video) {
        const desc = pathOr('', ['description'], video)
        return desc.length > 100
          ? desc.substring(0, 100) + '...'
          : desc
      },
      changeVideoVisibility ({ id, isVisible }) {
        this.updateVideo({ id, isVisible })
      },
      askForDeleteVideo ({ title, id }) {
        this.deleteConfirmationModal.visibility = true
        this.deleteConfirmationModal.video = { title, id }
      },
      hideDeleteConfirmationModal () {
        this.deleteConfirmationModal.visibility = false
        setTimeout(() => {
          this.deleteConfirmationModal.video = {
            title: '',
            id: ''
          }
        }, 200)
      },
      onDeleteVideo (videoId) {
        this.deleteVideo(videoId)
        this.hideDeleteConfirmationModal()
      },
      getDate (date) {
        return date
          ? moment(date).format('DD-MM-YYYY')
          : 'Unknown date'
      },
      getSpeakers (speakers) {
        return speakers && speakers.length
          ? speakers.reduce((speakersArr, speaker) => {
            speakersArr.push(speaker.name)
            return speakersArr
          }, []).join(', ')
          : 'Unknown speakers'
      }
    },
    async created () {
      await this.fetchVideos()
      await this.getSessionsList({fetchAll: true})
      await this.fetchSpeakersList()
      this.loaded = true
    }
  }
</script>

<style lang="scss" scoped>
  .video {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 10px;
    margin: 15px 0;

    &__info {
      display: flex;
    }

    &__desc {
      max-width: 70%;
      margin-left: 30px;
    }

    &__controls {
      display: flex;
      align-items: center;

      &-status {
        height: 30px;
        min-width: 165px;
      }
    }

    &-add-btn {
      margin-left: 0;
    }

    @media screen and (max-width: 600px) {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  .selected-related-session {
    margin-bottom: 20px;
  }
</style>



// WEBPACK FOOTER //
// src/components/pages/Videos/List/index.vue